/**
 * Easter bingo cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterBingoCardsPack: ToilePack = {
    slug: 'easter-bingo-cards',
    category: 'season',
    name: 'Easter bingo cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pictures', label: 'Picture bingo', subject: 'A bingo card with a 5 by 5 grid of small easter pictures, an egg in the centre free square, headed exactly "BINGO"'},
        {key: 'words', label: 'Word bingo', subject: 'A bingo card with a 5 by 5 grid of easter words, a centre square lettered exactly "FREE", a bunny border'},
        {key: 'calling', label: 'Calling cards', subject: 'A sheet of small square calling cards each with one easter picture and its name lettered exactly underneath, such as "EGG" or "CHICK", dashed cut lines, in a grid'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['with numbers instead of pictures', 'for a smaller group of six', 'as a two-player travel version'],
    directionHint: 'try: numbers instead of pictures',
    seo: {
        title: 'Easter Bingo Cards: Pictures, Words & Calling Cards',
        metaDescription: "Nine Easter bingo pieces — picture bingo, word bingo, and calling cards — in pastel, vintage, and kids' styles, ready to print for the party.",
        h1: 'Easter bingo cards: picture bingo, word bingo, calling cards',
        tagline: 'Nine pieces, three bingo formats, three styles each — ready to print for the party.',
        intro: "Three pieces for an Easter bingo party: a picture bingo card, a word bingo card, and a sheet of calling cards, each in pastel, vintage, and kids' cartoon styles. Every card prints exactly as lettered, ready to cut and play. Ask for a different picture set, a themed calling deck, or a bigger grid, and the same bingo grammar carries it.",
        useCases: [
            'a classroom Easter party',
            'a church or community egg hunt event',
            'a family Easter gathering',
            'a Sunday school activity',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each card is a single vertical page, close to US letter or A4 proportions — print at 100% for a clean grid.'},
            {q: 'Do I get one bingo card or several different ones?', a: 'Each render is one card design — print multiple copies, or generate a second layout, so every player has a different card.'},
            {q: 'Can I get a different picture set or grid size?', a: 'Yes — the direction field takes any variation, including a themed picture set or a different grid size.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
